import { PlatformManager } from './PlatformManager';
import { GameRegistry } from './GameRegistry';
import { ServerMessage } from '../network/protocol';

/**
 * UIController manages lobby, chat, user list, settings panel injection,
 * and all non-game UI interactions.
 */
export class UIController {
  private platform: PlatformManager;
  private isHostMode = false;
  private gameIsRunning = false;
  private excludedUsers = new Set<string>();
  private selectedGameId = '';
  private connectionStatusHandlersBound = false;

  constructor(platform: PlatformManager) {
    this.platform = platform;
  }

  init() {
    this.waitForDOM(() => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('room')) {
        // URL에 room이 있으면 게임 선택 건너뛰고 바로 로비 (참가 모드)
        document.getElementById('game-select')!.style.display = 'none';
        document.getElementById('lobby')!.style.display = 'flex';
        this.initLobby();
      } else {
        this.initGameSelect();
      }
    });
  }

  private waitForDOM(cb: () => void) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', cb);
    } else {
      cb();
    }
  }

  // ── Game Select ──

  private initGameSelect() {
    const gameList = document.getElementById('game-list')!;
    const games = GameRegistry.list();

    games.forEach((meta) => {
      const card = document.createElement('div');
      card.className = 'game-card';

      const name = document.createElement('div');
      name.className = 'game-card-name';
      name.textContent = meta.name;

      const desc = document.createElement('div');
      desc.className = 'game-card-desc';
      desc.textContent = meta.description;

      const players = document.createElement('div');
      players.className = 'game-card-players';
      players.textContent = `${meta.minPlayers}~${meta.maxPlayers}명`;

      card.appendChild(name);
      card.appendChild(desc);
      card.appendChild(players);

      card.addEventListener('click', () => {
        this.selectedGameId = meta.id;
        document.getElementById('game-select')!.style.display = 'none';
        document.getElementById('lobby')!.style.display = 'flex';
        document.getElementById('lobby-title')!.textContent = meta.name;
        this.initLobby();
      });

      gameList.appendChild(card);
    });
  }

  // ── Lobby ──

  private initLobby() {
    const nicknameInput = document.getElementById('lobby-nickname') as HTMLInputElement;
    const savedNick = localStorage.getItem('mbr_nickname');
    if (savedNick) nicknameInput.value = savedNick;

    // Check URL for room parameter
    const urlParams = new URLSearchParams(window.location.search);
    const roomFromUrl = urlParams.get('room');
    if (roomFromUrl) {
      (document.getElementById('lobby-roomid') as HTMLInputElement).value = roomFromUrl;
      // 초대 링크로 접속한 경우 방 만들기 버튼 및 구분선 숨김
      document.getElementById('btnCreateRoom')!.style.display = 'none';
      const divider = document.querySelector('.lobby-divider') as HTMLElement;
      if (divider) divider.style.display = 'none';
    }

    document.getElementById('btnCreateRoom')!.addEventListener('click', async () => {
      const nickname = nicknameInput.value.trim();
      if (!nickname) { this.showLobbyError('닉네임을 입력하세요'); return; }
      localStorage.setItem('mbr_nickname', nickname);
      this.showLobbyLoading(true);
      try {
        const roomId = await this.platform.createAndJoinRoom(nickname);
        this.enterGameScreen(roomId, true);
      } catch (e: any) {
        this.showLobbyError('방 생성에 실패했습니다: ' + e.message);
        this.showLobbyLoading(false);
      }
    });

    document.getElementById('btnJoinRoom')!.addEventListener('click', async () => {
      const nickname = nicknameInput.value.trim();
      const roomId = (document.getElementById('lobby-roomid') as HTMLInputElement).value.trim();
      if (!nickname) { this.showLobbyError('닉네임을 입력하세요'); return; }
      if (!roomId) { this.showLobbyError('방 코드를 입력하세요'); return; }
      localStorage.setItem('mbr_nickname', nickname);
      this.showLobbyLoading(true);
      try {
        await this.platform.joinRoom(roomId, nickname);
        this.enterGameScreen(roomId, false);
      } catch (e: any) {
        this.showLobbyError('방 참가에 실패했습니다: ' + e.message);
        this.showLobbyLoading(false);
      }
    });

    // Enter key support
    nicknameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') document.getElementById('btnCreateRoom')!.click();
    });
    document.getElementById('lobby-roomid')!.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') document.getElementById('btnJoinRoom')!.click();
    });
  }

  private showLobbyError(msg: string) {
    document.getElementById('lobby-error')!.textContent = msg;
  }

  private showLobbyLoading(show: boolean) {
    document.getElementById('lobby-loading')!.style.display = show ? 'block' : 'none';
  }

  // ── Enter Game Screen ──

  private async enterGameScreen(roomId: string, isHost: boolean) {
    this.isHostMode = isHost;
    document.getElementById('lobby')!.style.display = 'none';
    document.getElementById('game-screen')!.style.display = 'grid';

    // Host: set the selected game ID on the platform
    if (isHost && this.selectedGameId) {
      this.platform.setGameId(this.selectedGameId);
    }

    // Update URL with room code
    const url = new URL(window.location.href);
    url.searchParams.set('room', roomId);
    window.history.replaceState({}, '', url.toString());

    // Room bar
    document.getElementById('room-code-display')!.textContent = '방: ' + roomId;
    this.setConnectionStatus('online', '연결됨');
    this.bindConnectionStatusHandlers();

    // Copy link button
    document.getElementById('btnCopyRoomLink')!.addEventListener('click', () => {
      const link = window.location.origin + window.location.pathname + '?room=' + roomId;
      navigator.clipboard.writeText(link).then(() => {
        document.getElementById('btnCopyRoomLink')!.textContent = '복사됨!';
        setTimeout(() => {
          document.getElementById('btnCopyRoomLink')!.textContent = '링크 복사';
        }, 2000);
      });
    });

    // Leave button → 게임 선택 화면으로 돌아감
    document.getElementById('btnLeaveRoom')!.addEventListener('click', () => {
      this.platform.disconnect();
      this.setConnectionStatus('offline', '연결 끊김');
      document.getElementById('game-screen')!.style.display = 'none';
      document.getElementById('game-select')!.style.display = 'flex';
      this.showLobbyLoading(false);
      this.showLobbyError('');
      const leaveUrl = new URL(window.location.href);
      leaveUrl.searchParams.delete('room');
      window.history.replaceState({}, '', leaveUrl.toString());
    });

    // Setup chat
    this.initChat();

    // Setup server message handlers
    this.setupServerHandlers(isHost);

    // Determine game ID: host uses selectedGameId, viewer waits for joined message
    let gameId: string;
    if (isHost) {
      gameId = this.selectedGameId;
    } else {
      // Viewer: wait for joined message to get gameId from the room
      gameId = await new Promise<string>((resolve) => {
        const handler = (msg: ServerMessage) => {
          if (msg.type === 'joined' && msg.gameId) {
            resolve(msg.gameId);
          }
        };
        this.platform.client.onMessage(handler);
        // If platform already has a gameId (fallback)
        if (this.platform.gameId) {
          resolve(this.platform.gameId);
        }
      });
      this.platform.setGameId(gameId);
    }

    // Initialize game
    const container = document.getElementById('game-container')!;
    const game = await this.platform.initGame(gameId, container);

    if (isHost && game) {
      this.initHostControls(game);
    }
  }

  // ── Chat ──

  private initChat() {
    const chatInput = document.getElementById('chat-input') as HTMLInputElement;
    const btnSend = document.getElementById('btnChatSend')!;

    const sendChat = () => {
      const msg = chatInput.value.trim();
      if (!msg) return;
      this.platform.chat(msg);
      chatInput.value = '';
    };

    btnSend.addEventListener('click', sendChat);
    chatInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') sendChat();
    });
  }

  private addChatMessage(nickname: string, message: string, isSystem: boolean) {
    const container = document.getElementById('chat-messages')!;
    const div = document.createElement('div');
    div.className = 'chat-msg' + (isSystem ? ' system' : '');

    if (isSystem) {
      div.textContent = message;
    } else {
      const nameSpan = document.createElement('span');
      nameSpan.className = 'chat-nick';
      nameSpan.textContent = nickname;
      const msgSpan = document.createElement('span');
      msgSpan.className = 'chat-text';
      msgSpan.textContent = message;
      div.appendChild(nameSpan);
      div.appendChild(msgSpan);
    }

    container.appendChild(div);
    container.scrollTop = container.scrollHeight;

    // Limit messages
    while (container.children.length > 200) {
      container.removeChild(container.firstChild!);
    }
  }

  // ── User List ──

  private updateUserList(users: { nickname: string; isHost: boolean }[]) {
    const ul = document.getElementById('user-list-ul')!;
    ul.innerHTML = '';
    users.forEach((u) => {
      const li = document.createElement('li');
      const isExcluded = this.excludedUsers.has(u.nickname);

      if (u.isHost) {
        li.classList.add('host');
      }

      // Host sees toggle buttons for non-host users
      if (this.isHostMode && !u.isHost) {
        li.classList.add('toggleable');
        if (isExcluded) li.classList.add('excluded');

        const nameSpan = document.createElement('span');
        nameSpan.className = 'user-name';
        nameSpan.textContent = u.nickname;

        const toggleBtn = document.createElement('button');
        toggleBtn.className = 'user-toggle-btn ' + (isExcluded ? 'excluded' : 'included');
        toggleBtn.textContent = isExcluded ? '제외됨' : '참가중';
        toggleBtn.title = isExcluded ? '클릭하여 참가시키기' : '클릭하여 제외하기';

        toggleBtn.addEventListener('click', () => {
          this.toggleParticipant(u.nickname);
        });

        li.appendChild(nameSpan);
        li.appendChild(toggleBtn);
      } else {
        li.textContent = u.nickname + (u.isHost ? ' (호스트)' : '');
      }

      ul.appendChild(li);
    });
    document.getElementById('user-count-badge')!.textContent = String(users.length);

    const participantCount = users.filter(u => !this.excludedUsers.has(u.nickname)).length;
    document.getElementById('room-user-count')!.textContent =
      users.length + '명 접속 / ' + participantCount + '명 참가';
  }

  private toggleParticipant(nickname: string) {
    if (this.gameIsRunning) return;

    const game = this.platform.currentGame;
    if (!game) return;

    if (this.excludedUsers.has(nickname)) {
      this.excludedUsers.delete(nickname);
      (game as any).addParticipant?.(nickname);
    } else {
      this.excludedUsers.add(nickname);
      (game as any).removeParticipant?.(nickname);
      this.addChatMessage('', nickname + '님이 게임에서 제외되었습니다.', true);
    }
    this.updateUserList(this.platform.client.users);
  }

  // ── Server Message Handlers ──

  private setupServerHandlers(isHost: boolean) {
    this.platform.client.onMessage((msg: ServerMessage) => {
      switch (msg.type) {
        case 'joined':
          this.updateUserList(msg.users);
          this.addChatMessage('', '방에 입장했습니다.', true);
          if (!isHost && msg.gameState === 'running') {
            this.gameIsRunning = true;
            this.addChatMessage('', '게임이 진행 중입니다. 관전 모드로 시청합니다.', true);
          }
          break;
        case 'user_joined':
          this.updateUserList(msg.users);
          this.addChatMessage('', msg.nickname + '님이 입장했습니다.', true);
          if (isHost && !this.gameIsRunning) {
            this.autoAddParticipant(msg.nickname);
          }
          break;
        case 'user_left':
          this.updateUserList(msg.users);
          this.addChatMessage('', msg.nickname + '님이 퇴장했습니다.', true);
          if (isHost && !this.gameIsRunning) {
            this.autoRemoveParticipant(msg.nickname);
          }
          break;
        case 'chat':
          this.addChatMessage(msg.nickname, msg.message, false);
          break;
        case 'host_disconnected':
          this.addChatMessage('', '호스트가 연결을 끊었습니다.', true);
          this.gameIsRunning = false;
          break;
        case 'game_start':
          this.gameIsRunning = true;
          break;
        case 'game_finished':
          if (!isHost && msg.data?.winner) {
            this.addChatMessage('', '우승자: ' + msg.data.winner, true);
          }
          this.gameIsRunning = false;
          break;
        case 'game_reset':
          this.gameIsRunning = false;
          break;
      }
    });

    this.platform.addEventListener('disconnected', () => {
      this.setConnectionStatus('offline', '연결 끊김');
      this.addChatMessage('', '서버와 연결이 끊어졌습니다.', true);
      this.gameIsRunning = false;
    });
  }

  // ── Auto-add/remove participants (Host only) ──

  private bindConnectionStatusHandlers() {
    if (this.connectionStatusHandlersBound) return;
    this.connectionStatusHandlersBound = true;

    this.platform.client.addEventListener('reconnecting', (event: Event) => {
      const detail = (event as CustomEvent<{ attempt: number; delay: number }>).detail;
      const seconds = Math.max(1, Math.round((detail?.delay ?? 1000) / 1000));
      this.setConnectionStatus('reconnecting', `재연결 중 (${seconds}초)`);
      this.addChatMessage('', `연결이 끊겨 재연결을 시도합니다 (${detail?.attempt ?? 1}회차).`, true);
    });

    this.platform.client.addEventListener('reconnected', () => {
      this.setConnectionStatus('online', '연결 복구');
      this.addChatMessage('', '연결이 복구되었습니다.', true);
      setTimeout(() => {
        if (this.platform.client.isConnected) {
          this.setConnectionStatus('online', '연결됨');
        }
      }, 2000);
    });

    this.platform.client.addEventListener('disconnected', () => {
      if (!this.platform.client.isConnected) {
        this.setConnectionStatus('offline', '연결 끊김');
      }
    });
  }

  private setConnectionStatus(state: 'online' | 'reconnecting' | 'offline', text: string) {
    const el = document.getElementById('connection-status');
    if (!el) return;
    el.textContent = text;
    el.classList.remove('online', 'reconnecting', 'offline');
    el.classList.add(state);
  }

  private autoAddParticipant(nickname: string) {
    if (this.excludedUsers.has(nickname)) return;
    const game = this.platform.currentGame;
    if (!game) return;
    (game as any).addParticipant?.(nickname);
    this.addChatMessage('', nickname + '님이 참가자로 추가되었습니다.', true);
  }

  private autoRemoveParticipant(nickname: string) {
    const game = this.platform.currentGame;
    if (!game) return;
    (game as any).removeParticipant?.(nickname);
  }

  // ── Host Controls ──

  private initHostControls(game: import('./IGame').IGame) {
    const settingsPanel = document.getElementById('settings')!;
    settingsPanel.style.display = 'flex';

    // Inject game-specific settings HTML
    const settingsHTML = game.getSettingsHTML();
    if (settingsHTML) {
      settingsPanel.innerHTML = settingsHTML;
    }

    // Bind settings with host context
    const ctx = this.platform.createHostContext();
    game.bindSettings(settingsPanel, ctx);

    // Listen for game events to update chat
    game.addEventListener('game:started', () => {
      this.gameIsRunning = true;
      this.addChatMessage('', '게임이 시작되었습니다!', true);
    });

    game.addEventListener('game:finished', (e: Event) => {
      const detail = (e as CustomEvent).detail;
      this.gameIsRunning = false;
      this.addChatMessage('', '우승자: ' + detail.winner, true);
    });

    game.addEventListener('game:reset', () => {
      this.gameIsRunning = false;
    });

    // Listen for skill effects from the roulette engine
    // @ts-ignore - game-specific message handler
    if ((game as any).roulette) {
      // not needed; effects are handled within the game
    }
  }
}
